import { useConversationMessageMediaContentState } from '@modules/conversation/ConversationMediaContentView/useConversationMessageMediaContent';
import { useQueryClient } from '@tanstack/react-query';
import { isBefore, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { IChatConversation } from 'src/@types/chat';
import { ConversationMessageContentType } from 'src/@types/conversation';
import { useAuthContext } from 'src/auth/useAuthContext';
import { Iconify } from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { InfiniteData } from 'src/requests/todos';
import { useSocketEvent } from 'src/socket';
import {} from 'src/socket/listeners/chat';
import {
  onGatheringChatNewMessage,
  onGatheringDeleteMessage,
  onGatheringUpdateMessage,
} from 'src/socket/listeners/gathering';
import { QueryKey } from 'src/utils/react-query';
import { useGatheringChatState } from '../../../GatheringSessions/gatheringChatState';
import { AudioType, useAudio } from '../../../audio';
import { ActionsBarButtonStyled, StyledBadge } from '../../shared/styles';
import { Action } from '../../shared/types';
import { useGatheringChatQuery } from './conversation/useGatheringChatQuery';
import { getSameDateAndAuthor } from './conversation/utils';
import { IGatheringChatItem } from './types';

type Props = {
  isGuest?: boolean;
  sessionId: string;
  isOpened: boolean;
  toggleOpenAction: (action: Action, fromClose?: boolean) => void;
  disabled?: boolean;
};

export const GatheringChatButton = ({
  isGuest,
  sessionId,
  isOpened,
  toggleOpenAction,
  disabled,
}: Props) => {
  const { translate } = useLocales();
  const { user } = useAuthContext();
  const audio = useAudio();

  const { setSessionId, sessionId: openId } = useGatheringChatState();

  const { gatheringChat } = useGatheringChatQuery({ sessionRef: sessionId });

  const queryClient = useQueryClient();
  const queryKey = [QueryKey.GATHERING, sessionId, 'chatMessages'];

  const { checkInitialMediaContentDownload } =
    useConversationMessageMediaContentState();

  const handleClick = () => {
    const sameChat = sessionId === openId;
    setSessionId(sessionId);
    toggleOpenAction('gatheringChat', sameChat);
  };

  useSocketEvent(
    onGatheringChatNewMessage((message) => {
      if (message.sessionRef !== sessionId) return;

      // PRELOAD MEDIA

      if (
        message.contentType === ConversationMessageContentType.VIDEO &&
        !!message.mediaThumbnailRef
      ) {
        checkInitialMediaContentDownload(message, 'mediaThumbnailRef');
      }

      checkInitialMediaContentDownload(message);

      queryClient.setQueryData<InfiniteData<IGatheringChatItem[]>>(
        queryKey,
        (cache) => {
          if (!cache)
            return {
              pageParams: [undefined],
              pages: [[message]],
            };

          for (let i = 0; i < cache.pages.length; i += 1) {
            const list = cache.pages[i];

            for (let j = 0; j < list.length; j += 1) {
              if (list[j]._id === message._id) return { ...cache };

              if (
                isBefore(
                  parseISO(list[j].createdAt),
                  parseISO(message.createdAt),
                )
              ) {
                const previousMessage = list[j];
                const newMessage = getSameDateAndAuthor(
                  message,
                  previousMessage,
                );

                list.splice(j, 0, newMessage);
                return { ...cache };
              }
            }
          }

          cache.pages.at(-1)?.unshift(message);

          return { ...cache };
        },
        {
          updatedAt: Date.now(),
        },
      );

      if (message.sessionRef !== openId) {
        if (message.author === user?._id) return;

        queryClient.setQueryData<IChatConversation>(
          [QueryKey.GATHERING, sessionId, 'chat'],
          (cache) => {
            if (cache) {
              return {
                ...cache,
                unreadMessages: [...(cache?.unreadMessages || []), message._id],
              };
            }
          },
        );
      }

      audio.play(AudioType.CHANNEL_NEW_MESSAGE);
    }),
  );

  useSocketEvent(
    onGatheringUpdateMessage((message) => {
      if (message.sessionRef !== sessionId) return;
      queryClient.setQueryData<InfiniteData<IGatheringChatItem[]>>(
        queryKey,
        (messages) =>
          messages
            ? {
                ...messages,
                pages: [
                  ...messages.pages.map((page) => [
                    ...page.map((item) =>
                      item._id === message._id ? { ...item, ...message } : item,
                    ),
                  ]),
                ],
              }
            : messages,
        {
          updatedAt: Date.now(),
        },
      );
    }),
  );

  // TODO -> Ver se vamos remover a mensagem ou mostrar como mensagem excluída.
  // Atualmente estou removendo a mensagem.
  useSocketEvent(
    onGatheringDeleteMessage((message) => {
      if (message.sessionRef !== sessionId) return;
      queryClient.setQueryData<InfiniteData<IGatheringChatItem[]>>(
        queryKey,
        (messages) =>
          messages
            ? {
                ...messages,
                pages: [
                  ...messages.pages.map((page) => [
                    ...page.filter((item) => item._id !== message._id),
                  ]),
                ],
              }
            : messages,
        {
          updatedAt: Date.now(),
        },
      );
    }),
  );

  const unreadCount = useMemo(() => {
    if (!gatheringChat?.unreadMessages?.length) return 0;
    const count = gatheringChat.unreadMessages.length;
    return count > 0 ? (count > 9 ? '+9' : count) : 1;
  }, [gatheringChat?.unreadMessages?.length]);

  return (
    <StyledBadge
      color="primary"
      overlap="circular"
      badgeContent={unreadCount || ''}
      invisible={!unreadCount}
      sx={{
        '& > .MuiBadge-badge': {
          border: (theme) => `1px solid ${theme.palette.background.default}`,
        },
        cursor: 'pointer',
      }}
    >
      <ActionsBarButtonStyled
        variant="outlined"
        color={isOpened ? 'primary' : 'inherit'}
        onClick={handleClick}
        tooltip={translate('actions_bar.active_session_chat')}
        placement="left"
        disabled={disabled}
      >
        <Iconify icon="tabler:message" />
      </ActionsBarButtonStyled>
    </StyledBadge>
  );
};
